import { Component } from 'react';
import sharedStyles from '../single-page.module.scss';
import styles from './font-subsetter.component.module.scss';

type LibState =
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'ready'; opentype: any }
    | { kind: 'error'; message: string };

interface State {
    fontBuffer: ArrayBuffer | null;
    fontName: string;
    subsetText: string;
    outputUrl: string | null;
    inputSize: number;
    outputSize: number;
    lib: LibState;
    busy: boolean;
    error: string | null;
}

const PRESETS = {
    'Latin basic': 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789 .,!?:;\'"-_()[]{}@#$%&*+=/<>',
    'Latin extended': 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789 .,!?:;\'"-_()[]{}@#$%&*+=/<>àáâãäåæçèéêëìíîïðñòóôõöøùúûüýþÿÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖØÙÚÛÜÝÞ',
    'Numbers only': '0123456789.,-+/=:;',
    'Uppercase only': 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
};

export default class FontSubsetterComponent extends Component<{ feature?: string }, State> {
    state: State = {
        fontBuffer: null,
        fontName: '',
        subsetText: PRESETS['Latin basic'],
        outputUrl: null,
        inputSize: 0,
        outputSize: 0,
        lib: { kind: 'idle' },
        busy: false,
        error: null,
    };

    componentWillUnmount() {
        if (this.state.outputUrl) URL.revokeObjectURL(this.state.outputUrl);
    }

    private async ensureLib(): Promise<any | null> {
        if (this.state.lib.kind === 'ready') return this.state.lib.opentype;
        this.setState({ lib: { kind: 'loading' } });
        try {
            const mod = await import('opentype.js');
            const opentype = (mod as any).default || mod;
            this.setState({ lib: { kind: 'ready', opentype } });
            return opentype;
        } catch (e: any) {
            this.setState({ lib: { kind: 'error', message: e.message || 'Failed to load opentype.js' } });
            return null;
        }
    }

    onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const reader = new FileReader();
        reader.onload = () => {
            const buf = reader.result as ArrayBuffer;
            this.setState({ fontBuffer: buf, fontName: f.name, inputSize: buf.byteLength, outputUrl: null, error: null });
        };
        reader.readAsArrayBuffer(f);
    };

    subset = async () => {
        if (!this.state.fontBuffer) return;
        this.setState({ busy: true });
        const opentype = await this.ensureLib();
        if (!opentype) { this.setState({ busy: false }); return; }
        try {
            const font = opentype.parse(this.state.fontBuffer);
            const chars = Array.from(new Set(this.state.subsetText));
            const glyphs = chars.map((c: string) => font.charToGlyph(c)).filter(Boolean);
            // Include .notdef
            if (!glyphs.find((g: any) => g.name === '.notdef') && font.glyphs.get(0)) {
                glyphs.unshift(font.glyphs.get(0));
            }
            const subsetFont = new opentype.Font({
                familyName: font.names.fontFamily?.en || 'Subset',
                styleName: font.names.fontSubfamily?.en || 'Regular',
                unitsPerEm: font.unitsPerEm,
                ascender: font.ascender,
                descender: font.descender,
                glyphs,
            });
            const buf = subsetFont.toArrayBuffer();
            const blob = new Blob([buf], { type: 'font/ttf' });
            const url = URL.createObjectURL(blob);
            this.setState({ outputUrl: url, outputSize: blob.size, busy: false, error: null });
        } catch (e: any) {
            this.setState({ error: e.message || 'Subsetting failed', busy: false });
        }
    };

    download = () => {
        if (!this.state.outputUrl) return;
        const a = document.createElement('a');
        a.href = this.state.outputUrl;
        a.download = 'subset.ttf';
        a.click();
    };

    render() {
        const { fontBuffer, fontName, subsetText, outputUrl, inputSize, outputSize, lib, busy, error } = this.state;
        const savings = inputSize > 0 && outputSize > 0 ? Math.round((1 - outputSize / inputSize) * 100) : 0;
        return (
            <div className="row">
                <div className="col-12 col-md-6">
                    <div className={styles.fileBox}>
                        <input type="file" id="subset-font-file" accept=".ttf,.otf,font/*" className={styles.fileInput}
                            onChange={this.onFileChange} />
                        <label htmlFor="subset-font-file" className={sharedStyles.buttonPrimary}>Choose font (TTF / OTF)</label>
                        {fontName && <div className={styles.fileName}>{fontName} ({(inputSize / 1024).toFixed(1)} KB)</div>}
                    </div>
                    <label className={sharedStyles.label}>Characters to keep</label>
                    <div className={styles.unitRow}>
                        {Object.keys(PRESETS).map(k => (
                            <button key={k} className={styles.unitBtn}
                                onClick={() => this.setState({ subsetText: (PRESETS as any)[k] })}>{k}</button>
                        ))}
                    </div>
                    <textarea className={`${sharedStyles.textarea} ${styles.subsetInput}`} rows={5}
                        value={subsetText}
                        onChange={e => this.setState({ subsetText: e.target.value })} />
                    <button className={sharedStyles.buttonPrimary} onClick={this.subset}
                        disabled={!fontBuffer || busy || lib.kind === 'loading'}>
                        {busy ? 'Subsetting…' : lib.kind === 'loading' ? 'Loading opentype.js…' : 'Subset font'}
                    </button>
                    {(error || lib.kind === 'error') && (
                        <p className={styles.error} role="alert">{error || (lib as any).message}</p>
                    )}
                </div>
                <div className="col-12 col-md-6">
                    <label className={sharedStyles.label}>Output</label>
                    {outputUrl ? (
                        <div className={styles.resultBox}>
                            <div className={styles.resultLabel}>Subset font</div>
                            <div className={styles.resultMeta}>
                                {(outputSize / 1024).toFixed(1)} KB
                                <span style={{ marginLeft: '0.75rem', fontSize: '0.9rem', color: '#15803d' }}>
                                    −{savings}% smaller
                                </span>
                            </div>
                            <button className={sharedStyles.buttonPrimary} onClick={this.download}>
                                Download .ttf
                            </button>
                        </div>
                    ) : (
                        <div className={styles.placeholder}>Upload a TTF / OTF, pick a character set, and click Subset.</div>
                    )}
                </div>
            </div>
        );
    }
}
